import React, { useState, useEffect, useCallback } from "react";
import { Box, Paper, Typography, Switch, FormControlLabel, Alert, CircularProgress } from "@mui/material";
import { api } from "../../services/api";
import { useAuth } from "../../context/AuthContext";

interface EmailPreferencesPanelProps {
  token: string | null;
}

const EmailPreferencesPanel: React.FC<EmailPreferencesPanelProps> = ({ token }) => {
  const { user } = useAuth();
  const [dashboardEmailsEnabled, setDashboardEmailsEnabled] = useState(true);
  const [loading, setLoading] = useState(true);
  const [updating, setUpdating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [success, setSuccess] = useState<string | null>(null);

  const userEmail = user?.email;
  const isTeamMember = user?.userType === "team";

  // Fetch current preferences
  useEffect(() => {
    if (!userEmail || !token) {
      setLoading(false);
      return;
    }

    const fetchPreferences = async () => {
      try {
        setLoading(true);
        const response = await api.emailPreferences.getPreferences(token);
        
        // Find dashboard preference
        const dashboardPref = response.preferences.find(
          (p) => p.context === "dashboard"
        );
        
        if (dashboardPref) {
          setDashboardEmailsEnabled(!dashboardPref.unsubscribed);
        } else {
          // Default to enabled if no preference exists
          setDashboardEmailsEnabled(true);
        }
      } catch (err: any) {
        console.error("Error fetching email preferences:", err);
        setError("Failed to load email preferences");
      } finally {
        setLoading(false);
      }
    };

    fetchPreferences();
  }, [userEmail, token]);

  const handleToggleDashboardEmails = useCallback(async () => {
    if (!userEmail || !token) return;

    const newValue = !dashboardEmailsEnabled;
    setUpdating(true);
    setError(null);
    setSuccess(null);

    try {
      await api.emailPreferences.updatePreference(
        {
          context: "dashboard",
          unsubscribed: !newValue,
        },
        token
      );

      setDashboardEmailsEnabled(newValue);
      setSuccess(
        newValue
          ? "Dashboard email notifications enabled"
          : "Dashboard email notifications disabled"
      );
    } catch (err: any) {
      console.error("Error updating email preferences:", err);
      setError("Failed to update email preferences. Please try again.");
    } finally {
      setUpdating(false);
      // Clear success message after 3 seconds
      setTimeout(() => setSuccess(null), 3000);
    }
  }, [userEmail, token, dashboardEmailsEnabled]);

  if (!userEmail || isTeamMember) {
    return null;
  }

  return (
    <Paper sx={{ p: 3 }}>
      <Typography variant="h6" sx={{ mb: 2, fontWeight: 600 }}>
        Email Preferences
      </Typography>

      {error && (
        <Alert severity="error" sx={{ mb: 2 }} onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      {success && (
        <Alert severity="success" sx={{ mb: 2 }} onClose={() => setSuccess(null)}>
          {success}
        </Alert>
      )}

      {loading ? (
        <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
          <CircularProgress size={24} />
        </Box>
      ) : (
        <Box>
          <FormControlLabel
            control={
              <Switch
                checked={dashboardEmailsEnabled}
                onChange={handleToggleDashboardEmails}
                disabled={updating}
                color="primary"
              />
            }
            label={
              <Box>
                <Typography variant="body1" sx={{ fontWeight: 500 }}>
                  Receive dashboard emails
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  Get notified about account updates, team invites, and other dashboard activities
                </Typography>
              </Box>
            }
            sx={{ alignItems: "flex-start", mt: 1 }}
          />
        </Box>
      )}
    </Paper>
  );
};

export default EmailPreferencesPanel;
